import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Plus, RotateCw } from "lucide-react";
import { askForName, showErrorAlert } from "../../../../../components/HelperAlerts/HelpersAlerts.jsx";

const STATUS_TONE = {
    propuesta: "badge-info",
    confirmada: "badge-success",
    cancelada: "badge-error",
    completada: "badge-ghost",
    reemplazada: "badge-ghost",
};

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

const EMPTY_FORM = {
    custom_order_request: "", printer: "", orca_seconds: "", start_at: "", notes: "",
};

export default function ReservasSection() {
    const [reservations, setReservations] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [state, setState] = useState("loading");
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setState("loading");
        Promise.all([
            axios.get("/fulfillment-admin/reservations/"),
            axios.get("/fulfillment-admin/printers/"),
        ])
            .then(([reservationsRes, printersRes]) => {
                setReservations(Array.isArray(reservationsRes.data) ? reservationsRes.data : []);
                setPrinters(Array.isArray(printersRes.data) ? printersRes.data.filter((p) => p.is_active) : []);
                setState("ready");
            })
            .catch((err) => {
                console.error("Error loading reservations:", err);
                setState(err.response?.status === 403 ? "forbidden" : "error");
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const { data: estimate } = await axios.post(
                `/fulfillment-admin/custom-order-requests/${form.custom_order_request}/slicing-estimate/`,
                { orca_seconds: Number(form.orca_seconds) },
            );
            await axios.post("/fulfillment-admin/reservations/", {
                custom_order_request: Number(form.custom_order_request),
                printer: Number(form.printer),
                slicing_estimate: estimate.id,
                start_at: new Date(form.start_at).toISOString(),
                notes: form.notes,
            });
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            console.error("Error creating reservation:", err);
            showErrorAlert({ title: "No se pudo crear la reserva", text: formatApiError(err, "Revisa los datos.") });
        } finally {
            setSaving(false);
        }
    };

    const confirmReservation = async (reservation) => {
        try {
            await axios.post(`/fulfillment-admin/reservations/${reservation.id}/confirm/`);
            load();
        } catch (err) {
            showErrorAlert({ title: "No se pudo confirmar", text: formatApiError(err, "Inténtalo de nuevo.") });
        }
    };

    const cancelReservation = async (reservation) => {
        const reason = await askForName({
            title: "Motivo de cancelación",
            text: "Este motivo queda registrado.",
            confirmButtonText: "Cancelar reserva",
        });
        if (!reason) return;
        try {
            await axios.post(`/fulfillment-admin/reservations/${reservation.id}/cancel/`, { reason });
            load();
        } catch (err) {
            showErrorAlert({ title: "No se pudo cancelar", text: formatApiError(err, "Inténtalo de nuevo.") });
        }
    };

    const replanReservation = async (reservation) => {
        const newStart = await askForName({
            title: "Nueva fecha/hora",
            text: "Formato AAAA-MM-DDTHH:mm, p. ej. 2026-09-15T09:00",
            confirmButtonText: "Siguiente",
        });
        if (!newStart) return;
        const reason = await askForName({
            title: "Motivo de la replanificación",
            text: "El intervalo anterior se conserva en el historial.",
            confirmButtonText: "Replanificar",
        });
        if (!reason) return;
        try {
            await axios.post(`/fulfillment-admin/reservations/${reservation.id}/replan/`, {
                new_start_at: new Date(newStart).toISOString(), reason, notify_customer: true,
            });
            load();
        } catch (err) {
            showErrorAlert({ title: "No se pudo replanificar", text: formatApiError(err, "Revisa el hueco elegido.") });
        }
    };

    if (state === "loading") {
        return <div className="flex justify-center py-10"><span className="loading loading-spinner loading-lg text-primary" /></div>;
    }
    if (state === "forbidden") {
        return (
            <div role="alert" className="alert alert-warning text-sm">
                <AlertTriangle className="w-4 h-4" />
                Tu cuenta no tiene el permiso <code>fulfillment.manage_production_reservations</code>.
            </div>
        );
    }
    if (state === "error") {
        return (
            <div role="alert" className="alert alert-error text-sm">
                <AlertTriangle className="w-4 h-4" />
                No se pudieron cargar las reservas.
                <button type="button" className="btn btn-xs gap-1" onClick={load}><RotateCw className="w-3 h-3" />Reintentar</button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-base-content/60">
                    Calendario de producción. Ninguna acción de aquí inicia, pausa ni envía trabajos a una impresora.
                </p>
                <button type="button" className="btn btn-sm btn-primary gap-1" onClick={() => setShowForm((v) => !v)}>
                    <Plus className="w-4 h-4" /> Nueva reserva
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleCreate} className="glassCard rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">ID de solicitud (CustomOrderRequest)</span>
                        <input required type="number" className="input input-bordered input-sm" value={form.custom_order_request}
                            onChange={(e) => setForm((f) => ({ ...f, custom_order_request: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Impresora</span>
                        <select required className="select select-bordered select-sm" value={form.printer}
                            onChange={(e) => setForm((f) => ({ ...f, printer: e.target.value }))}>
                            <option value="">Elegir…</option>
                            {printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Duración Orca (segundos)</span>
                        <input required type="number" min="1" className="input input-bordered input-sm" value={form.orca_seconds}
                            onChange={(e) => setForm((f) => ({ ...f, orca_seconds: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Inicio</span>
                        <input required type="datetime-local" className="input input-bordered input-sm" value={form.start_at}
                            onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1 sm:col-span-2">
                        <span className="label-text text-xs">Notas</span>
                        <textarea className="textarea textarea-bordered textarea-sm" value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                    </label>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                            {saving ? <span className="loading loading-spinner loading-xs" /> : "Crear reserva"}
                        </button>
                    </div>
                </form>
            )}

            {reservations.length === 0 ? (
                <p className="text-sm text-base-content/50">No hay reservas todavía.</p>
            ) : (
                <div className="overflow-x-auto glassCard rounded-xl">
                    <table className="table table-zebra">
                        <thead>
                            <tr>
                                <th>Solicitud</th>
                                <th>Impresora</th>
                                <th>Inicio</th>
                                <th>Fin</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reservations.map((r) => (
                                <tr key={r.id}>
                                    <td>#{r.custom_order_request}</td>
                                    <td>{r.printer_detail?.name}</td>
                                    <td className="text-xs">{new Date(r.start_at).toLocaleString("es-ES")}</td>
                                    <td className="text-xs">{new Date(r.end_at).toLocaleString("es-ES")}</td>
                                    <td><span className={`badge badge-sm ${STATUS_TONE[r.status] || "badge-ghost"}`}>{r.status}</span></td>
                                    <td className="flex gap-1 flex-wrap">
                                        {r.status === "propuesta" && (
                                            <button type="button" className="btn btn-2xs btn-success" onClick={() => confirmReservation(r)}>Confirmar</button>
                                        )}
                                        {(r.status === "propuesta" || r.status === "confirmada") && (
                                            <>
                                                <button type="button" className="btn btn-2xs btn-outline" onClick={() => replanReservation(r)}>Replanificar</button>
                                                <button type="button" className="btn btn-2xs btn-error btn-outline" onClick={() => cancelReservation(r)}>Cancelar</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
